/**
 * TenantsTab — tenant directory: list, search, create, status change, detail drawer
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, Eye, Power, RefreshCw, UserCog, Package } from 'lucide-react';
import { toast } from 'sonner';
import { superAdminApi } from './superAdminApi';

const STATUS_COLORS = {
  active: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
  trial: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
  pending: 'bg-slate-500/20 text-slate-300 border-slate-400/30',
  suspended: 'bg-orange-500/20 text-orange-300 border-orange-400/30',
  expired: 'bg-rose-500/20 text-rose-300 border-rose-400/30',
  archived: 'bg-slate-700/40 text-slate-400 border-slate-600/30',
};

export const TenantsTab = () => {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const params = {};
  if (q) params.q = q;
  if (statusFilter !== 'all') params.status = statusFilter;

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['superadmin-tenants', params],
    queryFn: () => superAdminApi.listTenants(params),
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['superadmin-plans'],
    queryFn: () => superAdminApi.listPlans(false),
  });

  return (
    <div className="space-y-4" data-testid="superadmin-tenants-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <Input placeholder="Search name or code..." value={q} onChange={e => setQ(e.target.value)}
                 className="pl-9 bg-white/5 border-white/10 text-white" data-testid="tenants-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full max-w-[160px] bg-white/5 border-white/10 text-white" data-testid="tenants-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => qc.invalidateQueries({ queryKey: ['superadmin-tenants'] })}
                variant="outline" size="sm" className="text-white border-white/20 bg-white/5 hover:bg-white/10">
          <RefreshCw className="h-4 w-4" />
        </Button>
        <Button onClick={() => setCreateOpen(true)}
                className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="tenants-create-btn">
          <Plus className="h-4 w-4 mr-2" /> New Tenant
        </Button>
      </div>

      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left p-3">School</th>
                  <th className="text-left p-3">Code</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Plan</th>
                  <th className="text-right p-3">Students</th>
                  <th className="text-right p-3">Staff</th>
                  <th className="text-right p-3 pr-5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading...</td></tr>}
                {!isLoading && tenants.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">No tenants found.</td></tr>}
                {tenants.map(t => (
                  <tr key={t.tenant_id || t.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <p className="font-semibold">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.institution_type || 'school'}{t.country ? ` · ${t.country}` : ''}</p>
                    </td>
                    <td className="p-3 font-mono text-xs text-slate-300">{t.code}</td>
                    <td className="p-3">
                      <Badge variant="outline" className={`capitalize ${STATUS_COLORS[t.status] || STATUS_COLORS.pending}`}>
                        {t.status || 'unknown'}
                      </Badge>
                    </td>
                    <td className="p-3">
                      {t.plan_code
                        ? <Badge className="bg-indigo-500/20 text-indigo-200 border-indigo-400/30 capitalize">{t.plan_code}</Badge>
                        : <span className="text-slate-500 text-xs">No plan</span>}
                    </td>
                    <td className="p-3 text-right font-mono">{t.usage?.students ?? 0}</td>
                    <td className="p-3 text-right font-mono">{(t.usage?.staff ?? 0) + (t.usage?.teachers ?? 0)}</td>
                    <td className="p-3 text-right pr-5">
                      <Button size="sm" variant="outline" onClick={() => setDetailId(t.tenant_id || t.id)}
                              className="text-white border-white/20 bg-white/5 hover:bg-white/10"
                              data-testid={`tenant-detail-${t.tenant_id || t.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <CreateTenantDialog open={createOpen} onOpenChange={setCreateOpen} plans={plans} onCreated={() => {
        qc.invalidateQueries({ queryKey: ['superadmin-tenants'] });
        qc.invalidateQueries({ queryKey: ['superadmin-dashboard'] });
      }} />
      <TenantDetailDialog tenantId={detailId} onClose={() => setDetailId(null)} plans={plans} />
    </div>
  );
};

// ============== CREATE TENANT DIALOG ==============
const CreateTenantDialog = ({ open, onOpenChange, plans, onCreated }) => {
  const [form, setForm] = useState({
    name: '', code: '', institution_type: 'school', country: '', city: '',
    admin_contact_name: '', admin_contact_email: '', primary_color: '#1e3a5f',
    plan_code: 'trial', trial_days: 14,
  });

  const mut = useMutation({
    mutationFn: (data) => superAdminApi.createTenant(data),
    onSuccess: () => {
      toast.success('Tenant created');
      onOpenChange(false);
      onCreated?.();
      setForm({ ...form, name: '', code: '' });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Create failed'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 text-white border-white/10 max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create New Tenant</DialogTitle>
          <DialogDescription className="text-slate-400">Add a new school to the platform</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                   data-testid="tenant-form-name" className="bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <Label>Code *</Label>
            <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                   data-testid="tenant-form-code" placeholder="ABCD" className="bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.institution_type} onValueChange={v => setForm({ ...form, institution_type: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="school">School</SelectItem>
                <SelectItem value="college">College</SelectItem>
                <SelectItem value="university">University</SelectItem>
                <SelectItem value="bible_college">Bible College</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Plan</Label>
            <Select value={form.plan_code} onValueChange={v => setForm({ ...form, plan_code: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="tenant-form-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                {plans.map(p => <SelectItem key={p.code} value={p.code}>{p.name} — ${p.price_monthly}/mo</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Country</Label>
            <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })}
                   className="bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                   className="bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <Label>Admin Contact Name</Label>
            <Input value={form.admin_contact_name} onChange={e => setForm({ ...form, admin_contact_name: e.target.value })}
                   className="bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <Label>Admin Contact Email</Label>
            <Input type="email" value={form.admin_contact_email}
                   onChange={e => setForm({ ...form, admin_contact_email: e.target.value })}
                   className="bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <Label>Trial Days</Label>
            <Input type="number" value={form.trial_days}
                   onChange={e => setForm({ ...form, trial_days: parseInt(e.target.value) || 0 })}
                   className="bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <Label>Primary Color</Label>
            <Input type="color" value={form.primary_color}
                   onChange={e => setForm({ ...form, primary_color: e.target.value })}
                   className="bg-white/5 border-white/10 text-white h-10" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  className="text-white border-white/20 bg-white/5">Cancel</Button>
          <Button onClick={() => mut.mutate(form)} disabled={mut.isPending || !form.name || !form.code}
                  className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="tenant-form-submit">
            {mut.isPending ? 'Creating...' : 'Create Tenant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== TENANT DETAIL DIALOG ==============
const AVAILABLE_MODULES = [
  'admissions', 'sis', 'attendance', 'timetable', 'lms', 'exams', 'reports', 'library',
  'transport', 'hostel', 'access', 'communication', 'cms_website', 'alumni', 'finance',
  'hr_payroll', 'gamification', 'proctoring', 'scheduler', 'api_access',
];

const TenantDetailDialog = ({ tenantId, onClose, plans }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-tenant-detail', tenantId],
    queryFn: () => superAdminApi.tenantDetail(tenantId),
    enabled: !!tenantId,
  });

  const { data: settingsSummary } = useQuery({
    queryKey: ['superadmin-tenant-settings', tenantId],
    queryFn: () => superAdminApi.tenantSettingsSummary(tenantId),
    enabled: !!tenantId,
  });

  const { data: cmsSummary } = useQuery({
    queryKey: ['superadmin-tenant-cms', tenantId],
    queryFn: () => superAdminApi.tenantCmsSummary(tenantId),
    enabled: !!tenantId,
  });

  const statusMut = useMutation({
    mutationFn: ({ status, reason }) => superAdminApi.changeTenantStatus(tenantId, status, reason),
    onSuccess: () => {
      toast.success('Status updated');
      qc.invalidateQueries({ queryKey: ['superadmin-tenant-detail', tenantId] });
      qc.invalidateQueries({ queryKey: ['superadmin-tenants'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  const subMut = useMutation({
    mutationFn: (data) => superAdminApi.subscribe(tenantId, data),
    onSuccess: () => {
      toast.success('Subscription updated');
      qc.invalidateQueries({ queryKey: ['superadmin-tenant-detail', tenantId] });
      qc.invalidateQueries({ queryKey: ['superadmin-tenants'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  const modMut = useMutation({
    mutationFn: (modules) => superAdminApi.setModules(tenantId, modules),
    onSuccess: () => {
      toast.success('Modules updated');
      qc.invalidateQueries({ queryKey: ['superadmin-tenant-detail', tenantId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  const impersonateMut = useMutation({
    mutationFn: () => superAdminApi.impersonate(tenantId),
    onSuccess: (res) => {
      toast.success('Impersonation session logged');
      window.open(`/admin/schools/${tenantId}?impersonated=${res.session_id}`, '_blank');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [modules, setModules] = useState([]);

  React.useEffect(() => {
    if (data?.modules_enabled) setModules(data.modules_enabled);
    if (data?.subscription?.plan_code) setSelectedPlan(data.subscription.plan_code);
  }, [data]);

  if (!tenantId) return null;

  const t = data?.tenant || {};
  const sub = data?.subscription;
  const usage = data?.usage || {};
  const invoices = data?.invoices || [];

  return (
    <Dialog open={!!tenantId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 text-white border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {t.name || 'Tenant Detail'}
            <Badge variant="outline" className={`capitalize ${STATUS_COLORS[t.status] || ''}`}>{t.status}</Badge>
          </DialogTitle>
          <DialogDescription className="text-slate-400 font-mono">{t.code}</DialogDescription>
        </DialogHeader>

        {isLoading ? <p className="text-slate-500">Loading...</p> : (
          <div className="space-y-5">
            {/* Quick actions */}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ status: 'active', reason: '' })}
                      className="text-emerald-300 border-emerald-400/30 bg-emerald-500/10 hover:bg-emerald-500/20" data-testid="tenant-activate-btn">
                <Power className="h-4 w-4 mr-1" /> Activate
              </Button>
              <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ status: 'suspended', reason: 'Manual suspend' })}
                      className="text-orange-300 border-orange-400/30 bg-orange-500/10 hover:bg-orange-500/20" data-testid="tenant-suspend-btn">
                Suspend
              </Button>
              <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ status: 'archived', reason: 'Archived' })}
                      className="text-slate-400 border-slate-500/30 bg-slate-500/10 hover:bg-slate-500/20">
                Archive
              </Button>
              <Button size="sm" variant="outline" onClick={() => impersonateMut.mutate()}
                      className="text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 ml-auto" data-testid="tenant-impersonate-btn">
                <UserCog className="h-4 w-4 mr-1" /> View As Tenant
              </Button>
            </div>

            {/* Usage */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {['students', 'staff', 'teachers', 'lessons', 'exams'].map(k => (
                <div key={k} className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase">{k}</p>
                  <p className="text-xl font-bold mt-1">{usage[k] ?? 0}</p>
                </div>
              ))}
            </div>

            {/* Subscription */}
            <Card className="bg-white/5 border-white/10 text-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Subscription</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {sub ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge className="bg-indigo-500/20 text-indigo-200 border-indigo-400/30 capitalize">{sub.plan_code}</Badge>
                      <span className="ml-2 text-sm text-slate-400 capitalize">{sub.status} · {sub.billing_cycle}</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {sub.trial_ends_at && `Trial ends ${new Date(sub.trial_ends_at).toLocaleDateString()}`}
                    </p>
                  </div>
                ) : <p className="text-sm text-slate-500">No active subscription</p>}
                <div className="flex gap-2">
                  <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {plans.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={billingCycle} onValueChange={setBillingCycle}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={() => subMut.mutate({ plan_code: selectedPlan, billing_cycle: billingCycle, trial_days: 0 })}
                          className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="tenant-subscribe-btn">
                    Subscribe
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Modules */}
            <Card className="bg-white/5 border-white/10 text-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2"><Package className="h-4 w-4" /> Modules Access</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {AVAILABLE_MODULES.map(m => (
                    <label key={m} className="flex items-center gap-2 p-2 rounded hover:bg-white/5 cursor-pointer">
                      <Checkbox checked={modules.includes(m)} onCheckedChange={(v) => {
                        setModules(v ? [...modules, m] : modules.filter(x => x !== m));
                      }} data-testid={`tenant-module-${m}`} />
                      <span className="text-xs capitalize">{m.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
                <Button size="sm" onClick={() => modMut.mutate(modules)}
                        className="mt-3 bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="tenant-modules-save">
                  Save Modules ({modules.length})
                </Button>
              </CardContent>
            </Card>

            {/* Invoices */}
            <Card className="bg-white/5 border-white/10 text-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Recent Invoices</CardTitle>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 && <p className="text-sm text-slate-500">No invoices yet.</p>}
                {invoices.slice(0, 10).map(inv => (
                  <div key={inv.id} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                    <span className="font-mono text-xs">{inv.number}</span>
                    <span>${inv.amount}</span>
                    <Badge variant="outline" className="capitalize">{inv.status}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Settings Health (Phase 2) */}
            {settingsSummary && (
              <Card className="bg-white/5 border-white/10 text-white" data-testid="tenant-settings-summary">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Tenant Settings Health</span>
                    <Badge className={
                      settingsSummary.health_score >= 80 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30' :
                      settingsSummary.health_score >= 60 ? 'bg-amber-500/20 text-amber-300 border-amber-400/30' :
                      'bg-rose-500/20 text-rose-300 border-rose-400/30'
                    }>
                      {settingsSummary.health_score}/100
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <span className={settingsSummary.has_logo ? 'text-emerald-300' : 'text-slate-500'}>
                      {settingsSummary.has_logo ? '✓' : '✗'} Logo
                    </span>
                    <span className={settingsSummary.has_timezone ? 'text-emerald-300' : 'text-slate-500'}>
                      {settingsSummary.has_timezone ? '✓' : '✗'} Timezone
                    </span>
                    <span className={settingsSummary.has_smtp ? 'text-emerald-300' : 'text-slate-500'}>
                      {settingsSummary.has_smtp ? '✓' : '✗'} SMTP
                    </span>
                    <span className={settingsSummary.active_academic_years > 0 ? 'text-emerald-300' : 'text-slate-500'}>
                      {settingsSummary.active_academic_years > 0 ? '✓' : '✗'} Academic Year
                    </span>
                  </div>
                  {(settingsSummary.findings || []).length > 0 && (
                    <div className="space-y-1 pt-2 border-t border-white/5">
                      {settingsSummary.findings.map((f, i) => (
                        <p key={i} className={`text-xs ${f.severity === 'warning' ? 'text-amber-300' : 'text-slate-400'}`}>
                          {f.severity === 'warning' ? '⚠' : 'ℹ'} {f.msg}
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* CMS Oversight (Phase 3) */}
            {cmsSummary && (
              <Card className="bg-white/5 border-white/10 text-white" data-testid="tenant-cms-summary">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Website & CMS</span>
                    <Badge variant="outline" className={cmsSummary.website_published ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30' : 'bg-slate-500/20 text-slate-400 border-slate-400/30'}>
                      {cmsSummary.website_published ? 'Live' : 'Draft'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                    <div className="p-2 rounded bg-white/5">
                      <p className="text-slate-500">Pages</p>
                      <p className="font-bold text-lg">{cmsSummary.pages_published}<span className="text-slate-500 text-xs"> / {cmsSummary.pages_total}</span></p>
                    </div>
                    <div className="p-2 rounded bg-white/5">
                      <p className="text-slate-500">Announcements</p>
                      <p className="font-bold text-lg">{cmsSummary.announcements}</p>
                    </div>
                    <div className="p-2 rounded bg-white/5">
                      <p className="text-slate-500">Updated</p>
                      <p className="text-xs mt-1">{cmsSummary.last_updated ? new Date(cmsSummary.last_updated).toLocaleDateString() : 'Never'}</p>
                    </div>
                  </div>
                  <a href={cmsSummary.website_url} target="_blank" rel="noreferrer"
                     className="inline-block mt-3 text-xs text-indigo-300 hover:text-indigo-200 underline">
                    View tenant website →
                  </a>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
